/**
 * Multi-select cell: shows the selected values as removable tags and opens a
 * checkbox dropdown of the allowed options. The dropdown renders through
 * PositionedPortal (same as PillSelectCell) so it escapes the table's overflow.
 * Values not present in `options` still render as tags labelled with their raw value.
 */
import React, { useEffect, useRef, useState } from "react";
import "../../../../components/portal/portal.scss";
import PositionedPortal from "../../../../components/portal/PositionedPortal";
import ChevronDown from "../../../../svg/chevron-down";
import { CrossIcon } from "../../../../svg/cross-icon";

export interface MultiSelectTagCellOption {
  label: string;
  value: string;
}

export interface MultiSelectTagCellValue {
  /** Currently selected option values. */
  values: string[];
  options: MultiSelectTagCellOption[];
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Tags shown before the rest collapse into a "+N" chip. Defaults to all. */
  maxVisibleTags?: number;
}

interface MultiSelectTagCellProps {
  value: MultiSelectTagCellValue;
  rowData?: any;
  header?: string;
  onCellValueChange?: (rowData: any, fieldKey: string, newValue: any) => void;
}

/**
 * Resolves display labels for selected values, falling back to the raw value.
 *
 * @param options - Allowed options for the column
 * @param values - Currently selected values
 * @returns Options to render as tags
 */
const resolveSelected = (
  options: MultiSelectTagCellOption[],
  values: string[],
): MultiSelectTagCellOption[] => {
  const byValue = new Map(options.map((o) => [o.value, o]));
  return values.map((v) => byValue.get(v) ?? { value: v, label: v });
};

const MultiSelectTagCell: React.FC<MultiSelectTagCellProps> = ({
  value,
  rowData,
  header,
  onCellValueChange,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = resolveSelected(value.options, value.values ?? []);
  const maxVisible = value.maxVisibleTags ?? selected.length;
  const visible = selected.slice(0, maxVisible);
  const hidden = selected.slice(maxVisible);

  const emit = (next: string[]) =>
    onCellValueChange?.(rowData, header || "", next);

  const handleToggle = (optionValue: string) => {
    const current = value.values ?? [];
    emit(
      current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue],
    );
  };

  const renderDropdownContent = () => (
    <div
      ref={dropdownRef}
      className="portal-dropdown max-h-60 min-w-[11rem] overflow-auto rounded-md border border-primaryGray-14 bg-white shadow-md"
      onClick={(e) => e.stopPropagation()}
    >
      {value.options.length === 0 ? (
        <div className="px-3 py-2 text-tiny text-primaryGray-8">No options</div>
      ) : (
        value.options.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-tiny text-primaryGray-4 hover:bg-primaryGray-16"
          >
            <input
              type="checkbox"
              checked={(value.values ?? []).includes(opt.value)}
              onChange={() => handleToggle(opt.value)}
              className="h-3.5 w-3.5 rounded border-gray-300 accent-primaryGray-1"
              aria-label={`Select ${opt.label}`}
            />
            <span>{opt.label}</span>
          </label>
        ))
      )}
    </div>
  );

  return (
    <div ref={triggerRef} className="relative w-full">
      <div
        role="button"
        tabIndex={value.disabled ? -1 : 0}
        aria-label={value.ariaLabel || `Select ${header || "values"}`}
        onClick={() => !value.disabled && setOpen((p) => !p)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !value.disabled) {
            e.preventDefault();
            setOpen((p) => !p);
          }
        }}
        className={`flex min-h-[30px] w-full min-w-0 items-center justify-between gap-1 overflow-hidden rounded-md border border-primaryGray-15 px-2 py-1 text-tiny ${
          value.disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:border-primaryGray-12"
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {selected.length === 0 && (
            <span className="truncate text-primaryGray-12">
              {value.placeholder || "Select..."}
            </span>
          )}
          {visible.map((opt) => (
            <span
              key={opt.value}
              className="flex min-w-0 max-w-full flex-row items-center gap-1 rounded border border-primaryGray-1 bg-primaryGray-16 px-1.5 py-0.5 text-x-tiny font-medium text-primaryGray-1"
              title={opt.label}
            >
              <span className="truncate">{opt.label}</span>
              <span
                role="button"
                tabIndex={0}
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  emit((value.values ?? []).filter((v) => v !== opt.value));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    emit((value.values ?? []).filter((v) => v !== opt.value));
                  }
                }}
                className="flex cursor-pointer items-center justify-center rounded hover:bg-primaryGray-14"
              >
                <CrossIcon width="10" height="10" fillColor="#000000" />
              </span>
            </span>
          ))}
          {hidden.length > 0 && (
            <span
              className="flex-shrink-0 rounded border border-primaryGray-13 bg-primaryGray-16 px-1.5 py-0.5 text-x-tiny font-medium text-primaryGray-4"
              title={hidden.map((opt) => opt.label).join(", ")}
            >
              +{hidden.length}
            </span>
          )}
        </div>
        <ChevronDown
          rotate={open ? 180 : 0}
          stroke="#6b7280"
          width={10}
          height={10}
          className="flex-shrink-0"
        />
      </div>
      {open && (
        <PositionedPortal triggerRef={triggerRef} isOpen={open}>
          {renderDropdownContent()}
        </PositionedPortal>
      )}
    </div>
  );
};

export default MultiSelectTagCell;
